import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Plus, Users, ArrowRight, Building2, Pencil, Check, X, Trash2 } from "lucide-react";
import { api } from "../api";
import { toast } from "sonner";
import { useBusiness } from "../context/BusinessContext";

const BUSINESSES = ["A van Greunen", "NetMark Pro"];

function BusinessBadge({ value }) {
  const isNetMark = value === "NetMark Pro";
  return (
    <span
      data-testid="client-business-badge"
      className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-bold uppercase tracking-wider border"
      style={
        isNetMark
          ? { color: "#0F4F51", backgroundColor: "#E5FFFF", borderColor: "#00ACB3" }
          : { color: "#5B21B6", backgroundColor: "#EDE9FE", borderColor: "#A78BFA" }
      }
    >
      <Building2 className="w-3.5 h-3.5" strokeWidth={2.5} />
      {value}
    </span>
  );
}

export default function Clients() {
  const navigate = useNavigate();
  const { business, setBusiness } = useBusiness();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [name, setName] = useState("");
  const [newBusiness, setNewBusiness] = useState("A van Greunen");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState("");
  const [editBusiness, setEditBusiness] = useState("");

  const bizFilter = business;
  const setBizFilter = (v) => setBusiness(v);

  const load = async () => {
    const params = {};
    if (search) params.search = search;
    if (bizFilter) params.business = bizFilter;
    const r = await api.get("/clients", { params });
    setClients(r.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [search, bizFilter]);

  const addClient = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    setAdding(true);
    try {
      await api.post("/clients", { name: name.trim(), business: newBusiness });
      setName("");
      toast.success("Client added");
      load();
    } catch {
      toast.error("Could not add client");
    } finally { setAdding(false); }
  };

  const startEdit = (c) => { setEditingId(c.id); setEditBusiness(c.business); };
  const cancelEdit = () => { setEditingId(""); setEditBusiness(""); };
  const saveEdit = async (c) => {
    try {
      await api.patch(`/clients/${c.id}`, { business: editBusiness });
      toast.success("Updated");
      cancelEdit();
      load();
    } catch { toast.error("Failed to update"); }
  };

  const deleteClient = async (c) => {
    try {
      // Count tasks first so the warning is honest
      const r = await api.get("/tasks", { params: { client_id: c.id } });
      const taskCount = r.data.length;
      const msg = taskCount === 0
        ? `Delete ${c.name}? This cannot be undone.`
        : `Delete ${c.name}? This will also permanently delete ${taskCount} task${taskCount === 1 ? "" : "s"} and any remembered pricing for this client. This cannot be undone.`;
      if (!window.confirm(msg)) return;
      const res = await api.delete(`/clients/${c.id}`);
      const dt = res.data?.deleted_tasks || 0;
      toast.success(`${c.name} removed${dt ? ` (and ${dt} task${dt === 1 ? "" : "s"})` : ""}`);
      load();
    } catch (e) {
      toast.error("Could not delete client");
    }
  };

  const [allCounts, setAllCounts] = useState({ all: 0, "A van Greunen": 0, "NetMark Pro": 0 });
  useEffect(() => {
    // Compute counts independent of current filter for the chips
    api.get("/clients").then(r => {
      const all = r.data;
      setAllCounts({
        all: all.length,
        "A van Greunen": all.filter(c => c.business === "A van Greunen").length,
        "NetMark Pro": all.filter(c => c.business === "NetMark Pro").length,
      });
    });
  }, [clients]);

  const countBy = (b) => allCounts[b] || 0;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="mb-8">
        <div className="np-label mb-2">Step 1</div>
        <h1 className="text-3xl font-extrabold text-[#011617]" style={{ fontFamily: "Manrope" }}>Clients</h1>
        <p className="text-[#595959] mt-2">Only real clients live here. Tag each to either A van Greunen or NetMark Pro.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 np-card">
          {/* Filter chips */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button
              data-testid="clients-biz-filter-all"
              onClick={() => setBizFilter("")}
              className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                bizFilter === "" ? "bg-[#00ACB3] text-white" : "bg-white text-[#595959] border border-[#CCCCCC]"
              }`}
            >All ({allCounts.all})</button>
            {BUSINESSES.map(b => (
              <button
                key={b}
                data-testid={`clients-biz-filter-${b.replace(/\s/g, "-")}`}
                onClick={() => setBizFilter(b)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider ${
                  bizFilter === b ? "bg-[#00ACB3] text-white" : "bg-white text-[#595959] border border-[#CCCCCC]"
                }`}
              >{b} ({countBy(b)})</button>
            ))}
          </div>

          <div className="flex items-center gap-2 mb-4">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#595959]" />
              <input
                data-testid="client-search-input"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search clients..."
                className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] focus:ring-1 focus:ring-[#00ACB3] outline-none bg-white"
              />
            </div>
          </div>
          {clients.length === 0 ? (
            <p className="text-[#595959] text-sm py-6 text-center">No clients found.</p>
          ) : (
            <ul className="divide-y divide-[#CCCCCC]">
              {clients.map((c) => {
                const isEditing = editingId === c.id;
                return (
                  <li
                    key={c.id}
                    data-testid={`client-row-${c.id}`}
                    role={!isEditing ? "button" : undefined}
                    tabIndex={!isEditing ? 0 : -1}
                    onClick={(e) => {
                      if (isEditing) return;
                      // Ignore clicks on interactive children (handled below via stopPropagation)
                      navigate(`/workspace?client_id=${c.id}`);
                    }}
                    onKeyDown={(e) => {
                      if (!isEditing && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        navigate(`/workspace?client_id=${c.id}`);
                      }
                    }}
                    className={`py-3 px-3 -mx-3 rounded-lg flex items-center justify-between gap-3 flex-wrap transition ${
                      isEditing ? "cursor-default" : "cursor-pointer hover:bg-[#E5FFFF]/60 focus:bg-[#E5FFFF]/80 focus:outline-none focus:ring-2 focus:ring-[#00ACB3]"
                    }`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-9 h-9 rounded-lg bg-[#E5FFFF] flex items-center justify-center shrink-0">
                        <Users className="w-4 h-4 text-[#0F4F51]" />
                      </div>
                      <div className="min-w-0">
                        <div className="font-semibold text-[#011617] truncate">{c.name}</div>
                        {!isEditing && <div className="mt-1"><BusinessBadge value={c.business || "A van Greunen"} /></div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
                      {isEditing ? (
                        <>
                          <select
                            data-testid={`edit-biz-select-${c.id}`}
                            value={editBusiness}
                            onChange={(e) => setEditBusiness(e.target.value)}
                            className="px-2 py-1 rounded-lg border border-[#CCCCCC] outline-none focus:border-[#00ACB3] bg-white text-sm"
                          >
                            {BUSINESSES.map(b => <option key={b} value={b}>{b}</option>)}
                          </select>
                          <button data-testid={`save-biz-${c.id}`} onClick={() => saveEdit(c)} className="text-[#00ACB3] hover:text-[#0F4F51]"><Check className="w-4 h-4" /></button>
                          <button data-testid={`cancel-biz-${c.id}`} onClick={cancelEdit} className="text-[#595959] hover:text-[#011617]"><X className="w-4 h-4" /></button>
                        </>
                      ) : (
                        <>
                          <button
                            data-testid={`edit-biz-${c.id}`}
                            onClick={(e) => { e.stopPropagation(); startEdit(c); }}
                            className="text-xs font-semibold text-[#00ACB3] hover:text-[#0F4F51] inline-flex items-center gap-1"
                          ><Pencil className="w-3.5 h-3.5" /> Reassign</button>
                          <Link
                            to={`/workspace?client_id=${c.id}`}
                            data-testid={`view-client-${c.id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-sm font-semibold text-[#00ACB3] hover:text-[#0F4F51] inline-flex items-center gap-1"
                          >
                            Open workspace <ArrowRight className="w-4 h-4" />
                          </Link>
                          <button
                            data-testid={`delete-client-${c.id}`}
                            onClick={(e) => { e.stopPropagation(); deleteClient(c); }}
                            title="Remove client"
                            className="text-xs font-semibold text-[#991B1B] hover:text-[#7F1D1D] inline-flex items-center gap-1"
                          ><Trash2 className="w-3.5 h-3.5" /> Remove</button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="np-card h-fit">
          <h2 className="text-lg font-bold text-[#011617] mb-1" style={{ fontFamily: "Manrope" }}>Add a new client</h2>
          <p className="text-sm text-[#595959] mb-4">Use the real business name. Don't mix in task types.</p>
          <form onSubmit={addClient} className="space-y-3">
            <div>
              <label className="np-label block mb-1.5">Client name</label>
              <input
                data-testid="new-client-name-input"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Cape Dried Fruit"
                className="w-full px-3 py-2.5 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] focus:ring-1 focus:ring-[#00ACB3] outline-none bg-white"
              />
            </div>
            <div>
              <label className="np-label block mb-1.5">Belongs to</label>
              <select
                data-testid="new-client-business-select"
                value={newBusiness}
                onChange={(e) => setNewBusiness(e.target.value)}
                className="w-full px-3 py-2.5 rounded-lg border border-[#CCCCCC] focus:border-[#00ACB3] focus:ring-1 focus:ring-[#00ACB3] outline-none bg-white"
              >
                {BUSINESSES.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <button
              type="submit"
              disabled={adding || !name.trim()}
              data-testid="add-client-submit-btn"
              className="w-full bg-[#00ACB3] hover:bg-[#0F4F51] text-white font-semibold rounded-lg px-4 py-2.5 inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
            >
              <Plus className="w-4 h-4" /> Add client
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
